import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Social login buttons. Renders one button per backend-configured provider.
 *
 * Providers without credentials are automatically hidden — the backend
 * /api/auth/providers endpoint only returns enabled ones.
 *
 * Two flow shapes are supported:
 *   1. Google → Emergent-managed: redirect to https://auth.emergentagent.com/
 *      with our `/auth/social-finish` page as the return target. We read the
 *      session_id from the URL fragment + POST it back to the backend.
 *   2. Apple / Microsoft / Yahoo → standard OAuth redirect via the backend's
 *      /api/auth/social/{provider}/start endpoint.
 */
export default function SocialLoginButtons({ intent = "signin" }) {
  const [providers, setProviders] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/auth/providers")
      .then((r) => setProviders(r.data?.providers || []))
      .catch(() => setProviders([]))
      .finally(() => setLoaded(true));
  }, []);

  const onProviderClick = (slug) => {
    // Remember the return-to page for after the OAuth dance.
    try {
      const target = window.location.pathname + window.location.search;
      sessionStorage.setItem("fg_post_social_return", target);
    } catch { /* ignore */ }

    if (slug === "google") {
      // Phase D-8: direct Google OAuth (NO Emergent branding). Our backend
      // builds the Google authorize URL + handles state CSRF + callback.
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
      // THIS BREAKS THE AUTH.
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      window.location.href = `${backendUrl}/api/auth/google/login`;
      return;
    }
    // Standard code-flow providers — the backend issues the redirect.
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    window.location.href = `${backendUrl}/api/auth/social/${slug}/start`;
  };

  if (!loaded) return null;
  if (providers.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="social-login-buttons">
      <div className="space-y-2.5">
        {providers.map((p) => (
          <ProviderButton
            key={p.key}
            provider={p}
            intent={intent}
            onClick={() => onProviderClick(p.key)}
          />
        ))}
      </div>
      <div className="flex items-center gap-3 pt-1" role="separator" aria-label="or continue with email">
        <span className="h-px bg-emerald-100 flex-1" />
        <span className="text-[10px] tracking-[0.22em] uppercase text-emerald-700/55 font-semibold">
          or with email
        </span>
        <span className="h-px bg-emerald-100 flex-1" />
      </div>
    </div>
  );
}

function ProviderButton({ provider, intent, onClick }) {
  const verb = intent === "signup" ? "Sign up" : "Continue";
  const Icon = ICONS[provider.key] || GenericIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`social-${provider.key}-btn`}
      className="w-full flex items-center justify-center gap-3 rounded-full border border-emerald-100 bg-white py-3 text-sm font-semibold text-emerald-950 hover:bg-emerald-50/60 hover:border-emerald-200 transition shadow-[0_1px_3px_rgba(4,120,87,0.06)] active:scale-[0.99] touch-manipulation min-h-[48px]"
      aria-label={`${verb} with ${provider.label}`}
    >
      <Icon className="w-5 h-5 flex-shrink-0" aria-hidden />
      <span>{verb} with {provider.label}</span>
    </button>
  );
}

// Provider icons. Inline SVG, lightweight, brand-accurate.
function GoogleIcon(props) {
  return (
    <svg viewBox="0 0 48 48" {...props}>
      <path fill="#FFC107" d="M43.61 20.08H42V20H24v8h11.3a12 12 0 0 1-11.3 8 12 12 0 1 1 0-24 11.86 11.86 0 0 1 8.45 3.55l5.65-5.65A20 20 0 1 0 24 44a19.9 19.9 0 0 0 19.94-23.92z"/>
      <path fill="#FF3D00" d="M6.31 14.69l6.57 4.82A12 12 0 0 1 24 12a11.86 11.86 0 0 1 8.45 3.55l5.65-5.65A20 20 0 0 0 6.31 14.69z"/>
      <path fill="#4CAF50" d="M24 44a19.9 19.9 0 0 0 13.41-5.19l-6.19-5.24A11.9 11.9 0 0 1 24 36a12 12 0 0 1-11.27-7.94l-6.5 5A20 20 0 0 0 24 44z"/>
      <path fill="#1976D2" d="M43.61 20.08H42V20H24v8h11.3a12 12 0 0 1-4.09 5.57l6.19 5.24C36.97 38.92 44 32 44 24a20 20 0 0 0-.39-3.92z"/>
    </svg>
  );
}
function AppleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" {...props}>
      <path fill="#000000" d="M16.365 1.43c0 1.14-.47 2.226-1.23 3.027-.823.871-1.78 1.396-2.747 1.396-.034-1.119.45-2.187 1.244-3 .87-.892 2.07-1.466 2.733-1.423zM21 17.123c-.323.748-.473 1.082-.892 1.747-.583.93-1.4 2.085-2.41 2.094-.901.009-1.131-.59-2.353-.583-1.222.006-1.476.594-2.378.585-1.011-.009-1.781-1.05-2.363-1.98-1.633-2.6-1.804-5.65-.797-7.27.715-1.151 1.842-1.825 2.901-1.825 1.077 0 1.757.59 2.65.59.864 0 1.39-.591 2.638-.591.943 0 1.943.514 2.652 1.402-2.332 1.278-1.953 4.61.352 5.831z"/>
    </svg>
  );
}
function MicrosoftIcon(props) {
  return (
    <svg viewBox="0 0 23 23" {...props}>
      <path fill="#f25022" d="M1 1h10v10H1z"/>
      <path fill="#7fba00" d="M12 1h10v10H12z"/>
      <path fill="#00a4ef" d="M1 12h10v10H1z"/>
      <path fill="#ffb900" d="M12 12h10v10H12z"/>
    </svg>
  );
}
function YahooIcon(props) {
  return (
    <svg viewBox="0 0 32 32" {...props}>
      <path fill="#7B0099" d="M2 6h6.5l4.5 9.4L17.5 6H24l-7.5 14.5V26h-5v-5.5L2 6z"/>
      <circle cx="26" cy="13" r="2.4" fill="#7B0099"/>
    </svg>
  );
}
function GenericIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z"/>
    </svg>
  );
}

const ICONS = {
  google: GoogleIcon,
  apple: AppleIcon,
  microsoft: MicrosoftIcon,
  yahoo: YahooIcon,
};
